import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Lock, Bot, CheckCircle } from 'lucide-react';
import { calculateRowMetrics, getConnectColor } from './bulkEntryCalculations';

/**
 * A single row in the bulk pulse entry grid.
 * Memoized via React.memo to prevent re-renders when other rows change.
 *
 * Renders 17 columns:
 *   1 Source (sticky)
 *   11 Input cells (dialed, co.ivr, fd phone, connected, v.sched, v.done, cl.exp, rev.exp, cl.phone, cl.counter, comments)
 *   5 Auto-calc display cells (connect%, schedule%, done%, total closure, closure%)
 *
 * @param {string} sourceId - The source identifier
 * @param {string} sourceName - Display name of the source
 * @param {boolean} sourceNeedsVisit - Whether this source requires visit scheduling
 * @param {Object} rowData - The row form data (string values + metadata)
 * @param {Object} errors - Validation errors for this row (Record<field, errorMessage>)
 * @param {Function} onCellChange - Callback: (sourceId, field, value) => void
 * @param {Object} autoFilledFields - Fields that were auto-filled from WTF data (for blue highlighting)
 */
const BulkEntryRow = React.memo(function BulkEntryRow({
  sourceId,
  sourceName,
  sourceNeedsVisit,
  rowData,
  errors,
  onCellChange,
  autoFilledFields = {},
  isAutoGenerated = false,
  autoConfirmed = false,
  autoLocked = false,
  graceWindowExpiresAt = null,
  onConfirmEntry = null,
  entryId = null,
  autoFulfilled = false,
  autoFulfilledNeedsReview = false,
}) {
  const [focusedField, setFocusedField] = useState(null);
  const [minutesLeft, setMinutesLeft] = useState(0);

  // Grace window countdown
  useEffect(() => {
    if (!graceWindowExpiresAt || autoConfirmed || autoLocked) return;
    const update = () => {
      const left = Math.max(0, Math.floor((new Date(graceWindowExpiresAt) - new Date()) / 60000));
      setMinutesLeft(left);
    };
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [graceWindowExpiresAt, autoConfirmed, autoLocked]);

  if (!rowData) return null;

  const safeErrors = errors || {};
  const hasErrors = Object.keys(safeErrors).length > 0;
  const isExisting = !!rowData._existingEntryId;

  // Check if row is empty (no numeric fields have values)
  const isEmpty = !rowData.dialed && !rowData.connected && !rowData.visit_scheduled &&
    !rowData.visit_done && !rowData.closure_expected && !rowData.closure_phone &&
    !rowData.closure_counter && !rowData.revenue_expected;

  // Calculate auto-calc metrics
  const metrics = calculateRowMetrics(rowData);

  // Determine sticky cell background to match row background
  const stickyBg = hasErrors ? 'bg-red-50' : isExisting && !hasErrors ? 'bg-blue-50' : 'bg-white';

  /**
   * Render a numeric input cell.
   * Error shown as a red popup ABOVE the input when the cell has focus.
   * @param {string} field - Field name
   * @param {Object} opts - Options (step, width class)
   */
  const renderInputCell = (field, opts = {}) => {
    const { step = '1', type = 'number', minVal = '0' } = opts;
    const hasError = !!safeErrors[field];
    const isFocused = focusedField === field;
    const isAutoFilled = field in autoFilledFields;
    const isDisabled = autoLocked;

    return (
      <td className="px-1 py-2 text-center relative" style={{ minWidth: 60 }}>
        {hasError && isFocused && (
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-30
                          bg-red-600 text-white text-xs font-bold rounded px-2 py-1
                          whitespace-nowrap shadow-lg pointer-events-none">
            {safeErrors[field]}
            <div className="absolute top-full left-1/2 -translate-x-1/2
                            border-4 border-transparent border-t-red-600" />
          </div>
        )}
        {/* WTF badge for auto-filled cells */}
        {isAutoFilled && (
          <div className="absolute top-0 right-0 bg-blue-600 text-white text-[9px] px-1 rounded-bl font-bold z-10"
               title="Auto-filled from verified WTF data">
            WTF
          </div>
        )}
        <input
          type={type}
          min={type === 'number' ? minVal : undefined}
          step={type === 'number' ? step : undefined}
          disabled={isDisabled}
          className={cn(
            'h-9 w-full text-center text-sm border rounded px-1 focus:outline-none focus:ring-1 focus:ring-blue-400 hide-spinner',
            hasError ? 'border-red-500 bg-red-50' : isAutoFilled ? 'bg-blue-500/20 border-blue-400/50' : 'border-gray-300',
            isDisabled && 'bg-gray-100 text-gray-400 cursor-not-allowed'
          )}
          value={rowData[field] || ''}
          onChange={(e) => onCellChange(sourceId, field, e.target.value)}
          onFocus={() => setFocusedField(field)}
          onBlur={() => setFocusedField(null)}
        />
      </td>
    );
  };

  /**
   * Render an auto-calculated display cell (non-input).
   */
  const renderCalcCell = (value, opts = {}) => {
    const { color, bold, suffix = '', bgClass = 'bg-gray-50' } = opts;
    return (
      <td className={cn('px-2 py-2 text-center text-sm font-semibold', bgClass)}
        style={color ? { color } : undefined}
      >
        {bold ? <strong>{value}{suffix}</strong> : <>{value}{suffix}</>}
      </td>
    );
  };

  return (
    <tr className={cn(
      'border-b transition-colors',
      hasErrors && 'bg-red-50',
      isExisting && !hasErrors && 'bg-blue-50',
      isEmpty && 'opacity-60',
      autoLocked && 'opacity-60'
    )}>
      {/* Column 1: Source (sticky left) */}
      <td className={cn(
        'sticky left-0 z-10 border-r-2 border-gray-300 px-3 py-3 font-medium text-sm whitespace-nowrap',
        stickyBg
      )}>
        <div className="flex items-center gap-1.5">
          {autoLocked && <Lock size={12} className="text-red-500" />}
          {sourceName}
          {sourceNeedsVisit && (
            <span className="text-xs text-gray-400">(Visit)</span>
          )}
          {isExisting && !isAutoGenerated && (
            <Badge variant="outline" className="text-xs py-0 px-1">Edit</Badge>
          )}
        </div>
        {/* Auto-generation badges */}
        {isAutoGenerated && (
          <div className="flex items-center gap-1.5 mt-1">
            {!autoConfirmed && !autoLocked && (
              <>
                <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300 text-[10px] py-0 px-1">
                  <Bot size={10} className="mr-0.5" /> Auto
                </Badge>
                <span className="text-[10px] text-amber-600">
                  {minutesLeft > 0 ? `${minutesLeft}m left` : 'Expiring...'}
                </span>
                {onConfirmEntry && (
                  <button
                    onClick={() => onConfirmEntry(entryId || rowData._existingEntryId, null)}
                    className="text-[10px] px-1.5 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Confirm
                  </button>
                )}
              </>
            )}
            {autoLocked && (
              <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 text-[10px] py-0 px-1">
                <Lock size={10} className="mr-0.5" /> Locked
              </Badge>
            )}
            {autoConfirmed && (
              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300 text-[10px] py-0 px-1">
                <CheckCircle size={10} className="mr-0.5" /> Confirmed
              </Badge>
            )}
            {autoFulfilled && (
              <Badge variant="outline" className={cn(
                "text-[10px] py-0 px-1",
                autoFulfilledNeedsReview
                  ? "bg-amber-100 text-amber-700 border-amber-300"
                  : "bg-emerald-100 text-emerald-700 border-emerald-300"
              )}>
                {autoFulfilledNeedsReview ? 'Needs Verify' : 'Auto-Fulfilled'}
              </Badge>
            )}
          </div>
        )}
      </td>

      {/* Column 2: Dialed */}
      {renderInputCell('dialed')}

      {/* Column 3: Company IVR */}
      {renderInputCell('dialed_company_ivr')}

      {/* Column 4: FD Phone */}
      {renderInputCell('dialed_fd_phone')}

      {/* Column 5: Connected */}
      {renderInputCell('connected')}

      {/* Column 6: Connect% (auto-calc) */}
      {renderCalcCell(metrics.connectPct, {
        color: getConnectColor(metrics.connectPct),
        suffix: '%',
      })}

      {/* Column 7: Visit Scheduled */}
      {renderInputCell('visit_scheduled')}

      {/* Column 8: Visit Done */}
      {renderInputCell('visit_done')}

      {/* Column 9: Schedule Rate% (auto-calc) */}
      {renderCalcCell(metrics.scheduleRate, { suffix: '%' })}

      {/* Column 10: Visit Done Rate% (auto-calc) */}
      {renderCalcCell(metrics.visitDoneRate, { suffix: '%' })}

      {/* Column 11: Closure Expected */}
      {renderInputCell('closure_expected')}

      {/* Column 12: Revenue Expected (allows decimals) */}
      {renderInputCell('revenue_expected', { step: '0.01' })}

      {/* Column 13: Closure Phone */}
      {renderInputCell('closure_phone')}

      {/* Column 14: Closure Counter */}
      {renderInputCell('closure_counter')}

      {/* Column 15: Total Closure (auto-calc, bold green) */}
      {renderCalcCell(metrics.totalClosure, {
        bold: true,
        color: '#28A745',
        bgClass: 'bg-green-50',
      })}

      {/* Column 16: Closure Rate% (auto-calc) */}
      {renderCalcCell(metrics.closureRate, { suffix: '%' })}

      {/* Column 17: Comments (text input) */}
      <td className="px-1 py-2 text-center">
        <input
          type="text"
          className="h-9 w-24 text-sm border border-gray-300 rounded px-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
          value={rowData.comments || ''}
          onChange={(e) => onCellChange(sourceId, 'comments', e.target.value)}
        />
      </td>
    </tr>
  );
});

export default BulkEntryRow;
